import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TEAM_PAGE_LIVE } from '../config/siteFlags';

const INK = '#002A38';
const MUTED = '#57534E';
const TERRA = '#0076A3';
const LINE = '#ECE7DE';
const SOFT = '#FFE7D9';

const PRODUCTS = [
  { name: 'Screening', path: '/products/screening' },
  { name: 'Assessments', path: '/products/assessments' },
  { name: 'AI Interviews', path: '/products/interviews' },
  { name: 'JD Builder', path: '/jd-builder' },
  { name: 'Candidate Portal', path: '/products/engage' },
  { name: 'Talent Pipeline', path: '/products/pipeline' },
  { name: 'Compliance', path: '/products/compliance' },
  { name: 'Blockchain & NFT', path: '/products/blockchain' },
];

const FEATURES = [
  { name: 'Certified Assessments', path: '/features/certified-assessments' },
  { name: 'Anti-Cheating', path: '/features/anti-cheating' },
  { name: 'Fraud Prevention', path: '/features/fraud-prevention' },
  { name: 'NFT Credentials', path: '/features/nft-credentials' },
  { name: 'Verified Credentials & LinkedIn', path: '/features/verified-credentials' },
  { name: 'Bias-Free Scoring', path: '/features/bias-free-scoring' },
  { name: 'Resume Intelligence', path: '/features/resume-intelligence' },
  { name: 'AI Questions', path: '/features/ai-questions' },
  { name: 'Integrations', path: '/features/integrations' },
];

const ABOUT = [
  { name: 'Our Story', path: '/about' },
  ...(TEAM_PAGE_LIVE ? [{ name: 'Leadership Team', path: '/about/team' }] : []),
];

const itemTestId = (menuKey, path) => `nav-${menuKey}-item-${path.split('/').pop()}`;

export default function PublicNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const NavDropdown = ({ label, items, menuKey, path }) => (
    <div style={{ position: 'relative' }} onMouseEnter={() => setOpenMenu(menuKey)} onMouseLeave={() => setOpenMenu(null)}>
      <button
        data-testid={`nav-${menuKey}-trigger`}
        onClick={() => (path ? navigate(path) : setOpenMenu(openMenu === menuKey ? null : menuKey))}
        aria-haspopup="true" aria-expanded={openMenu === menuKey}
        aria-current={items.some((i) => isActive(i.path)) ? 'page' : undefined}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, padding: '0.5rem 0',
          color: items.some((i) => isActive(i.path)) ? TERRA : MUTED, display: 'flex', alignItems: 'center', gap: '0.3rem',
          transition: 'color 0.2s',
        }}>
        {label}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ transform: openMenu === menuKey ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {openMenu === menuKey && (
        <div style={{ position: 'absolute', top: '100%', left: -12, paddingTop: 8, zIndex: 100 }}>
          <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 16, padding: '0.5rem', boxShadow: '0 20px 44px -20px rgba(28,25,23,0.28)', minWidth: 224 }}>
            {items.map((item) => (
              <button key={item.path} onClick={() => { navigate(item.path); setOpenMenu(null); }}
                data-testid={itemTestId(menuKey, item.path)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '0.55rem 0.8rem', borderRadius: 10, border: 'none',
                  background: isActive(item.path) ? SOFT : 'transparent', color: isActive(item.path) ? TERRA : MUTED,
                  cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, transition: 'background 0.15s, color 0.15s',
                }}
                onMouseEnter={(e) => { if (!isActive(item.path)) { e.currentTarget.style.background = '#F6F1EA'; e.currentTarget.style.color = INK; } }}
                onMouseLeave={(e) => { if (!isActive(item.path)) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = MUTED; } }}>
                {item.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const LinkBtn = ({ label, path, testid }) => (
    <button data-testid={testid} onClick={() => navigate(path)}
      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, padding: '0.5rem 0', color: isActive(path) ? TERRA : MUTED, transition: 'color 0.2s' }}>
      {label}
    </button>
  );

  return (
    <nav style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 clamp(1rem, 4vw, 3rem)', height: 68,
      background: 'rgba(253,251,247,0.82)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
      borderBottom: `1px solid ${LINE}`, position: 'sticky', top: 0, zIndex: 50,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '2.25rem' }}>
        <div data-testid="nav-logo" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src="/chire-logo-full.png" alt="CHIRE" style={{ height: 46, objectFit: 'contain' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.6rem' }} className="desktop-only">
          <NavDropdown label="Products" items={PRODUCTS} menuKey="products" />
          <NavDropdown label="Features" items={FEATURES} menuKey="features" />
          <LinkBtn label="Pricing" path="/pricing" testid="nav-pricing-link" />
          <LinkBtn label="Case Study" path="/case-study" testid="nav-casestudy-link" />
          <NavDropdown label="About" items={ABOUT} menuKey="about" path="/about" />
          <LinkBtn label="Psychometrics" path="/psychometrics" testid="nav-psychometrics-link" />
          <LinkBtn label="Connect" path="/connectwithus" testid="nav-connect-link" />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1.1rem' }} className="desktop-only">
        <button data-testid="nav-signin" onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>Sign In</button>
        <button data-testid="nav-start-trial" onClick={() => navigate('/login?mode=register&redirect=payment&plan=starter_monthly')}
          style={{ background: 'linear-gradient(135deg, #00A0E0 0%, #F05000 100%)', color: '#fff', borderRadius: 999, fontWeight: 600, fontSize: '0.875rem', padding: '0.62rem 1.4rem', border: 'none', cursor: 'pointer', boxShadow: '0 10px 24px -12px rgba(240,80,0,0.55)', transition: 'transform 0.2s, opacity 0.2s' }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.95'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'none'; }}>
          Subscribe to Starter
        </button>
      </div>

      <button className="mobile-only" data-testid="nav-mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)}
        style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: '0.375rem', color: INK }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
      </button>

      {mobileOpen && (
        <div style={{ position: 'absolute', top: 68, left: 0, right: 0, background: '#fff', borderBottom: `1px solid ${LINE}`, padding: '0.75rem', boxShadow: '0 12px 28px -12px rgba(28,25,23,0.2)', zIndex: 100, maxHeight: '72vh', overflow: 'auto' }}>
          <div style={{ fontSize: '0.625rem', fontWeight: 700, color: TERRA, textTransform: 'uppercase', padding: '0.5rem 0.75rem', letterSpacing: '0.12em' }}>Products</div>
          {PRODUCTS.map((p) => (
            <button key={p.path} onClick={() => { navigate(p.path); setMobileOpen(false); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.55rem 0.75rem', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem', color: MUTED, borderRadius: 8 }}>{p.name}</button>
          ))}
          <div style={{ fontSize: '0.625rem', fontWeight: 700, color: TERRA, textTransform: 'uppercase', padding: '0.5rem 0.75rem', marginTop: '0.5rem', letterSpacing: '0.12em' }}>Features</div>
          {FEATURES.map((f) => (
            <button key={f.path} onClick={() => { navigate(f.path); setMobileOpen(false); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.55rem 0.75rem', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem', color: MUTED, borderRadius: 8 }}>{f.name}</button>
          ))}
          <button onClick={() => { navigate('/psychometrics'); setMobileOpen(false); }} data-testid="nav-psychometrics-link-mobile"
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.6rem 0.75rem', border: 'none', background: SOFT, cursor: 'pointer', fontSize: '0.9rem', color: TERRA, fontWeight: 700, borderRadius: 8, marginTop: '0.6rem' }}>
            Psychometrics — What is DISC?
          </button>
          <div style={{ fontSize: '0.625rem', fontWeight: 700, color: TERRA, textTransform: 'uppercase', padding: '0.5rem 0.75rem', marginTop: '0.5rem', letterSpacing: '0.12em' }}>About</div>
          {ABOUT.map((a) => (
            <button key={a.path} onClick={() => { navigate(a.path); setMobileOpen(false); }} data-testid={`${itemTestId('about', a.path)}-mobile`}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.55rem 0.75rem', border: 'none', background: isActive(a.path) ? SOFT : 'transparent', cursor: 'pointer', fontSize: '0.9rem', color: isActive(a.path) ? TERRA : MUTED, borderRadius: 8 }}>{a.name}</button>
          ))}
          <div style={{ borderTop: `1px solid ${LINE}`, marginTop: '0.5rem', paddingTop: '0.6rem', display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => { navigate('/case-study'); setMobileOpen(false); }} data-testid="nav-casestudy-link-mobile" style={{ flex: 1, padding: '0.65rem', border: `1px solid ${LINE}`, borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: '0.875rem', color: MUTED }}>Case Study</button>
            <button onClick={() => { navigate('/connectwithus'); setMobileOpen(false); }} data-testid="nav-connect-link-mobile" style={{ flex: 1, padding: '0.65rem', border: `1px solid ${LINE}`, borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: '0.875rem', color: MUTED }}>Connect</button>
            <button onClick={() => { navigate('/pricing'); setMobileOpen(false); }} style={{ flex: 1, padding: '0.65rem', border: `1px solid ${LINE}`, borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: '0.875rem', color: MUTED }}>Pricing</button>
            <button onClick={() => { navigate('/login'); setMobileOpen(false); }} style={{ flex: 1, padding: '0.65rem', border: 'none', borderRadius: 10, background: INK, cursor: 'pointer', fontSize: '0.875rem', color: '#fff', fontWeight: 600 }}>Sign In</button>
          </div>
        </div>
      )}
    </nav>
  );
}
